import { useEffect, useRef, useState } from 'react';
import { useChat } from '../hooks/useChat';
import { useMyPlayerId } from '../hooks/useIsHost';

interface Props {
  compact?: boolean;
}

export default function Chat({ compact = false }: Props) {
  const { messages, sendMessage } = useChat();
  const myId = useMyPlayerId();
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [unread, setUnread] = useState(0);
  const prevMessageCount = useRef(messages.length);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    const newMessages = messages.length - prevMessageCount.current;
    prevMessageCount.current = messages.length;
    if (!isOpen && newMessages > 0) {
      setUnread((u) => u + newMessages);
    }
  }, [messages.length]);

  const handleSend = () => {
    if (input.trim()) {
      sendMessage(input);
      setInput('');
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  if (compact) {
    return (
      <div className="fixed right-4 bottom-4 z-[100] flex flex-col items-end gap-2">
        {isOpen && (
          <div className="flex w-72 flex-col overflow-hidden rounded-xl bg-black/90 ring-1 ring-white/20 backdrop-blur-sm sm:w-80">
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
              <span className="text-xs font-semibold tracking-wider text-white/50 uppercase">Chat</span>
              <button onClick={toggleOpen} className="text-white/40 hover:text-white">
                ✕
              </button>
            </div>
            <div ref={scrollRef} className="flex h-48 flex-col gap-1 overflow-y-auto p-2">
              {messages.length === 0 && <span className="py-4 text-center text-xs text-white/30">Brak wiadomości</span>}
              {messages.map((msg, i) => (
                <div
                  key={`${msg.timestamp}-${i}`}
                  className={`rounded-lg px-2 py-1 text-sm ${
                    msg.playerId === myId ? 'bg-blue-500/20 text-blue-200' : 'bg-white/5 text-white/80'
                  }`}>
                  <span className="font-semibold text-white/60">{msg.playerName}: </span>
                  {msg.text}
                </div>
              ))}
            </div>
            <div className="flex border-t border-white/10">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Napisz wiadomość..."
                maxLength={200}
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder-white/30 outline-none"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="px-3 text-sm text-white/60 transition-colors hover:text-white disabled:text-white/20">
                Wyślij
              </button>
            </div>
          </div>
        )}
        <button
          onClick={toggleOpen}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-xl text-white/70 ring-1 ring-white/20 transition-all hover:bg-white/20 hover:text-white">
          {isOpen ? '✕' : '💬'}
          {!isOpen && unread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </div>
    );
  }

  // Full inline chat (for lobby)
  return (
    <div className="flex w-full flex-col overflow-hidden rounded-xl bg-black/70 ring-1 ring-white/10 backdrop-blur-sm">
      <div className="border-b border-white/10 px-4 py-2">
        <span className="text-xs font-semibold tracking-wider text-white/50 uppercase">Chat</span>
      </div>
      <div ref={scrollRef} className="flex h-40 flex-col gap-1 overflow-y-auto p-3">
        {messages.length === 0 && <span className="py-4 text-center text-xs text-white/30">Brak wiadomości</span>}
        {messages.map((msg, i) => (
          <div
            key={`${msg.timestamp}-${i}`}
            className={`rounded-lg px-2 py-1 text-sm ${
              msg.playerId === myId ? 'bg-blue-500/20 text-blue-200' : 'bg-white/5 text-white/80'
            }`}>
            <span className="font-semibold text-white/60">{msg.playerName}: </span>
            {msg.text}
          </div>
        ))}
      </div>
      <div className="flex border-t border-white/10">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Napisz wiadomość..."
          maxLength={200}
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim()}
          className="px-4 text-sm text-white/60 transition-colors hover:text-white disabled:text-white/20">
          Wyślij
        </button>
      </div>
    </div>
  );
}
